import instantsearch from 'instantsearch.js';
import { configure, searchBox, hits, clearRefinements } from 'instantsearch.js/es/widgets';
import searchClient from '@pratt/js/components/algolia/search-client';
import { customPagination } from '@pratt/js/components/algolia/pagination';
import { refinementListWithPanel } from '@pratt/js/components/algolia/panel';
import { refinementListTemplate } from '@pratt/js/components/algolia/templates/refinement-list';
import { createMiddleware } from '@pratt/js/components/algolia/insights';
import { createSearchRouting } from '@pratt/js/components/algolia/search-router';

class GraduatingStudentsSearch {
  constructor(rootEl) {
    this.rootEl = rootEl;
    if (!this.rootEl) {
      throw new Error('Root search element not found.');
    }

    this.hitsPerPage = 24;

    this.search = instantsearch({
      indexName: this.rootEl.dataset['algoliaIndex'],
      searchClient: searchClient,
      routing: createSearchRouting(this.rootEl.dataset['algoliaIndex']),
      insights: true,
      future: {
        preserveSharedStateOnUnmount: true,
      },
    });

    try {
      const insightsMiddleware = createMiddleware('Graduating Students');
      this.search.use(insightsMiddleware);
    } catch (error) {
      console.error('Failed to initialize Algolia Insights middleware:', error);
    }

    // Add refinement panel containers.
    this.refinementListContainer = this.rootEl.querySelector('#filters-content');
    ['js-algolia-index-search-schools-input', 'js-algolia-index-search-degree-and-program-input'].forEach((refinementPanel) => {
      const panel = document.createElement('div');
      panel.classList.add(refinementPanel, 'archive-refinement-list');
      this.refinementListContainer.appendChild(panel);
    })

    this.init();
    this.search.start();
  }

  init() {
    this.search.addWidgets([
      // https://www.algolia.com/doc/api-reference/widgets/configure/js/
      // Parameter options: https://www.algolia.com/doc/api-reference/search-api-parameters/
      configure({
        hitsPerPage: this.hitsPerPage,
        distinct: true,
        // Force faceting to be applied after de-duplication (via the Distinct setting)
        facetingAfterDistinct: true,
      }),

      searchBox({
        container: '.js-algolia-index-search-input',
        placeholder: 'Search',
        showSubmit: false,
      }),

      clearRefinements({
        container: '.js-algolia-index-search-clear',
        templates: {
          resetLabel: 'Reset',
        },
      }),

      refinementListWithPanel('School')({
        container: '.js-algolia-index-search-schools-input',
        attribute: 'schools',
        operator: 'and',
        limit: 10,
        showMore: true,
        showMoreLimit: 100,
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
      }),

      refinementListWithPanel('Degree and Program')({
        container: '.js-algolia-index-search-degree-and-program-input',
        attribute: 'degreeAndProgram',
        operator: 'and',
        limit: 10,
        showMore: true,
        showMoreLimit: 100,
        sortBy: ['name:asc'],
        templates: {
          item: refinementListTemplate,
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/hits/js/
      hits({
        container: '.js-algolia-index-search-hits',
        templates: {
          item(hit) {
            return `
              <div class="person-hit">
                <div class="person-hit__content">
                  <div class="person-hit__titles">
                    <p class="person-hit__name">
                      <span class="person-hit__name-text">
                        ${hit.diplomaName}
                      </span>
                    </p>
                  </div>
                  <div class="person-hit__meta">
                    <p class="person-hit__school">${hit.schools}</p>
                    <p class="person-hit__degree">${hit.degreeAndProgram}</p>
                  </div>
                </div>
              </div>
            `;
          },
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/pagination/js/
      customPagination({
        container: document.querySelector('.js-algolia-index-search-pagination'),
      }),
    ]);
  }
}

export default GraduatingStudentsSearch;
